/**
 * A provider record shown as LABELLED ROWS, with the raw text behind a disclosure.
 *
 * WHY THIS EXISTS. Four places in the Info panel and one on run detail rendered a provider's
 * record as `JSON.stringify(x, null, 2)` inside a `<pre>`. That is not a presentation of a
 * measurement, it is a refusal to present one: the reader is handed the transport encoding and
 * left to find the field that answers their question. The panel already knew better in one spot
 * — the X-header table it renders as key/value rows — so this generalises the pattern that was
 * already in the tree rather than inventing a second one.
 *
 * THE RAW TEXT IS KEPT, DELIBERATELY, behind `<details>`. It is the evidence: when a structured
 * row looks wrong the next question is always "what did the provider actually send", and a
 * cleanup that removed the answer would trade one diagnostic problem for another. Collapsed, not
 * deleted.
 *
 * KEYS ARE DISCOVERED, NOT LISTED, for the same reason the status-history render discovers them:
 * a listed set goes stale silently, and a provider that starts reporting one more field should
 * surface it without an edit here. `labels` only OVERRIDES the display name of a key it knows;
 * an unknown key still renders under its own wire name, which is what keeps this honest for a
 * provider nobody has hand-labelled yet.
 */
import type { CSSProperties, ReactNode } from 'react'

export interface StructuredRecordProps {
  /** The record as the API sent it. A non-object (or null) renders as the empty state. */
  record: unknown
  /** Display names for keys this caller knows. Unknown keys keep their wire name. */
  labels?: Record<string, string>
  /** What to say when there is nothing to show. Never rendered as an empty box. */
  emptyText?: string
  /** Caption for the disclosure holding the raw text. */
  rawLabel?: string
  theme: { bg: string; bgElevated: string; border: string; text: string }
}

function isRenderableRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Scalars render as themselves; a nested object or array keeps its JSON, inline. */
function renderValue(value: unknown): ReactNode {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number' || typeof value === 'string') return String(value)
  // A nested structure is still shown rather than summarised as [object Object], which is
  // what a naive String() would have produced — an empty-looking cell over real data.
  return JSON.stringify(value)
}

export function StructuredRecord({
  record,
  labels = {},
  emptyText = 'The provider returned no record.',
  rawLabel = 'Raw response',
  theme,
}: StructuredRecordProps) {
  if (!isRenderableRecord(record)) {
    return <p style={{ fontSize: '0.8rem', color: '#9aa0a6', margin: 0 }}>{emptyText}</p>
  }

  const entries = Object.entries(record)
  if (entries.length === 0) {
    return <p style={{ fontSize: '0.8rem', color: '#9aa0a6', margin: 0 }}>{emptyText}</p>
  }

  const keyCell: CSSProperties = {
    padding: '0.3rem 0.5rem 0.3rem 0',
    fontFamily: 'monospace',
    color: '#82b1ff',
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
  }
  const valueCell: CSSProperties = {
    padding: '0.3rem 0',
    fontFamily: 'monospace',
    wordBreak: 'break-word',
  }

  return (
    <>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key} style={{ borderTop: `1px solid ${theme.border}` }}>
              <td style={keyCell}>
                {labels[key] ?? key}
                {/* The VENDOR field name stays visible beside a friendly label, because a
                    support ticket quotes the vendor's name and a glossary term cannot be
                    searched for in the provider's own documentation. */}
                {labels[key] ? (
                  <span
                    style={{
                      display: 'block',
                      color: '#9aa0a6',
                      fontSize: '0.66rem',
                      fontWeight: 400,
                    }}
                  >
                    {key}
                  </span>
                ) : null}
              </td>
              <td style={valueCell}>{renderValue(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <details style={{ marginTop: '0.5rem' }}>
        <summary style={{ fontSize: '0.72rem', color: '#9aa0a6', cursor: 'pointer' }}>
          {rawLabel}
        </summary>
        <pre
          style={{
            background: theme.bgElevated,
            color: theme.text,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            padding: '0.75rem',
            fontSize: '0.7rem',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: '0.4rem 0 0',
          }}
        >
          {JSON.stringify(record, null, 2)}
        </pre>
      </details>
    </>
  )
}
